<template>
    <!-- Vue 2's two-pass keyed patch removes departing rows before inserting new
         ones. A normal tbody moves every retained row ahead of the removed head,
         triggering blur and layout for each move. No animation is requested. -->
    <transition-group tag="tbody" :css="false" name="timesheet-rows">
        <template v-for="item in segments">
            <tr v-if="item.type === 'spacer'" :key="item.key" class="timesheet-virtual-spacer" aria-hidden="true">
                <td :colspan="api.monthTableColumnCount" :style="{height: `${item.height}px`}"></td>
            </tr>
            <tr v-else-if="item.type === 'group'" :key="item.key" :data-virtual-key="item.key" class="timesheet-month-table__group-row">
                <th :colspan="api.monthTableColumnCount" scope="rowgroup">{{ item.label }}</th>
            </tr>
            <timesheet-month-row v-else :key="item.key" :data-virtual-key="item.key"
                :api="api" :line="item.line" :line-index="item.index" />
        </template>
        <slot />
    </transition-group>
</template>

<script>
import TimesheetMonthRow from './TimesheetMonthRow.vue';
import {rowOffsets, retainedWindow, segmentsInRange} from '@/utils/timesheetVirtualRows';

export default {
    name: 'TimesheetMonthRows',
    components: {TimesheetMonthRow},
    props: {api: {type: Object, required: true}, lines: {type: Array, required: true}},
    data: () => ({top: 0, height: 500, heights: {}, revealKey: null, windowRange: null}),
    computed: {
        items() {
            const result = [];
            let group = null;
            this.lines.forEach((line, index) => {
                const next = this.api.lineGroupKey(line);
                if (next !== group) { result.push({type: 'group', key: `group:${next}`, label: this.api.lineGroupLabel(line)}); group = next; }
                result.push({type: 'line', key: `line:${line.id}`, line, index});
            });
            return result;
        },
        offsets() { return rowOffsets(this.items, this.heights); },
        pinnedKey() {
            const match = String(this.api.editingPresenceKey || '').match(/^(?:entry|detail):(\d+):/);
            return match ? `line:${match[1]}` : null;
        },
        segments() {
            return segmentsInRange(this.items, this.offsets, this.windowRange || retainedWindow(this.offsets, 0, 500),
                [this.pinnedKey, this.revealKey]);
        }
    },
    watch: {
        lines() { this.schedule(); },
        'api.visibleMonthColumns'() { this.heights = {}; this.schedule(); },
        'api.visibleCharacteristicFields'() { this.heights = {}; this.schedule(); },
        'api.isMobileFullMonthView'() { this.heights = {}; this.schedule(); }
    },
    mounted() {
        if (typeof ResizeObserver !== 'undefined') {
            this._observer = new ResizeObserver(this.schedule);
            this._observer.observe(this.api.$refs.monthScroll);
        }
        window.addEventListener('resize', this.schedule);
        this.schedule();
    },
    beforeUpdate() {
        const control = document.activeElement;
        // Moving a keyed row around a spacer can blur an otherwise retained input.
        if (control && this.$el.contains(control)) {
            this._focusedControl = {control, start: control.selectionStart, end: control.selectionEnd};
        } else if (control !== document.body) {
            this._focusedControl = null;
        }
    },
    updated() {
        const retained = this._focusedControl;
        this._focusedControl = null;
        if (retained && retained.control.isConnected && document.activeElement === document.body) {
            retained.control.focus({preventScroll: true});
            if (retained.start != null && retained.control.setSelectionRange) {
                retained.control.setSelectionRange(retained.start, retained.end);
            }
        }
        this.schedule();
    },
    beforeDestroy() {
        if (this._frame) cancelAnimationFrame(this._frame);
        if (this._observer) this._observer.disconnect();
        window.removeEventListener('resize', this.schedule);
    },
    methods: {
        schedule() {
            if (this._frame) return;
            this._frame = requestAnimationFrame(() => { this._frame = null; this.measure(); });
        },
        geometry() {
            const viewport = this.api.$refs.monthScroll;
            if (!viewport || !this.$el) return null;
            const header = this.$el.parentElement.tHead;
            const headerHeight = header ? header.getBoundingClientRect().height : 0;
            const offset = this.$el.getBoundingClientRect().top - viewport.getBoundingClientRect().top + viewport.scrollTop;
            return {viewport, headerHeight, offset};
        },
        measure() {
            const geometry = this.geometry();
            if (!geometry) return;
            const {viewport, headerHeight, offset} = geometry;
            const top = Math.max(0, viewport.scrollTop + headerHeight - offset);
            const height = Math.max(46, viewport.clientHeight - headerHeight);
            // Geometry corrections must not reverse the reserve after measuring a
            // new row. Only actual scrolling chooses its direction.
            if (this._lastScrollTop != null && Math.abs(viewport.scrollTop - this._lastScrollTop) > 0.5) {
                this._scrollDirection = viewport.scrollTop > this._lastScrollTop ? 1 : -1;
            }
            this._lastScrollTop = viewport.scrollTop;
            if (Math.abs(this.top - top) > 0.5) this.top = top;
            if (Math.abs(this.height - height) > 0.5) this.height = height;
            const measured = {};
            Array.from(this.$el.children).forEach(row => {
                const key = row.getAttribute('data-virtual-key');
                const value = row.getBoundingClientRect().height;
                if (key && value > 0 && Math.abs((this.heights[key] || 0) - value) > 0.5) measured[key] = value;
            });
            if (Object.keys(measured).length) this.heights = Object.assign({}, this.heights, measured);
            const range = retainedWindow(this.offsets, top, height, this._scrollDirection || 1, this.windowRange);
            if (!this.windowRange || range.start !== this.windowRange.start || range.end !== this.windowRange.end) {
                this.windowRange = range;
            }
        },
        async reveal(lineId) {
            const index = this.items.findIndex(item => item.key === `line:${lineId}`);
            const geometry = this.geometry();
            if (index < 0 || !geometry) return false;
            const key = `line:${lineId}`;
            this.revealKey = key;
            const y = this.offsets[index];
            const bottom = this.offsets[index + 1];
            if (y < this.top || bottom > this.top + this.height) {
                geometry.viewport.scrollTop = Math.max(0, geometry.offset - geometry.headerHeight + y);
            }
            this.measure();
            await this.$nextTick();
            // Newly measured rows may differ from the estimated heights used above.
            const row = this.$el.querySelector(`[data-virtual-key="${key}"]`);
            if (row) {
                const bounds = row.getBoundingClientRect();
                const view = geometry.viewport.getBoundingClientRect();
                if (bounds.top < view.top + geometry.headerHeight || bounds.bottom > view.top + geometry.viewport.clientHeight) {
                    geometry.viewport.scrollTop += bounds.top - view.top - geometry.headerHeight;
                }
            }
            this.measure();
            await this.$nextTick();
            if (this.revealKey === key) this.revealKey = null;
            return true;
        }
    }
};
</script>

<style>
.timesheet-page .timesheet-month-scroll { overflow-anchor: none; }
.timesheet-page .timesheet-month-table .timesheet-virtual-spacer td {
    padding: 0; border: 0; line-height: 0; pointer-events: none;
}
</style>
